import { point, polygon, lineString, featureCollection, feature } from '@turf/helpers';
import { arcgisToGeoJSON } from '@esri/arcgis-to-geojson-utils';
import proj4 from 'proj4';
export const createPointFeature = ({ coordinates, id, properties, lat, long, ...rest }) => {
	if (coordinates && (lat || long)) {
		throw new Error(
			'Invalid parameters - Provide either a coordinates parameter OR lat and long parameters.'
		);
	}

	if (!coordinates && (lat === undefined || long === undefined)) {
		throw new Error('The coordinates parameter OR lat and long parameters are required.');
	}
	const coords = coordinates ? coordinates : [long, lat];
	const geoJSONPoint = point(coords, properties, { id });
	return {
		...geoJSONPoint,
		...rest
	};
};

export const createPolygonFeature = ({ coordinates, id, properties, ...rest }) => {
	const geoJSONPolygon = polygon(coordinates, properties, { id });
	return {
		...geoJSONPolygon,
		...rest
	};
};

export const createLineStringFeature = ({ coordinates, id, properties, ...rest }) => {
	const geoJSONLineString = lineString(coordinates, properties, { id });
	return {
		...geoJSONLineString,
		...rest
	};
};

export const createFeatureCollection = ({ features, id, ...rest }) => {
	const geoJSONFeatureCollection = featureCollection(features, { id });
	return {
		...geoJSONFeatureCollection,
		...rest
	};
};

export const createGeoJSONFeatureLayer = ({ type, id, feature, paint, layout }) => {
	return {
		type,
		id,
		source: {
			type: 'geojson',
			data: feature
		},
		paint: {
			...paint
		},
		layout: {
			...layout
		}
	};
};

/**
 * Normalize a FeatureCollection of Point, LineString, or Polygon features into a single
 * MultiPoint, MultiLineString, or MultiPolygon. If it's already normalized, returns as-is.
 */
export function normalizeToMultiGeometry(fc) {
	if (!fc || fc.type !== 'FeatureCollection' || !Array.isArray(fc.features)) {
		throw new Error('Input must be a FeatureCollection');
	}

	const features = fc.features;
	if (features.length === 0) return fc;

	const geomType = features[0].geometry?.type;

	// Handle already normalized Multi* geometries
	if (
		features.length === 1 &&
		['MultiPolygon', 'MultiPoint', 'MultiLineString'].includes(geomType)
	) {
		return fc;
	}

	// Normalize Polygons
	const polygonFeatures = features.filter(f => f.geometry?.type === 'Polygon');
	if (polygonFeatures.length > 1) {
		const coordinates = polygonFeatures.map(f => f.geometry.coordinates);
		const props = polygonFeatures[0].properties || {};
		return featureCollection([
			feature(
				{
					type: 'MultiPolygon',
					coordinates
				},
				props
			)
		]);
	}

	// Normalize Points
	const pointFeatures = features.filter(f => f.geometry?.type === 'Point');
	if (pointFeatures.length > 1) {
		const coordinates = pointFeatures.map(f => f.geometry.coordinates);
		const props = pointFeatures[0].properties || {};
		return featureCollection([
			feature(
				{
					type: 'MultiPoint',
					coordinates
				},
				props
			)
		]);
	}

	// Normalize LineStrings
	const lineFeatures = features.filter(f => f.geometry?.type === 'LineString');
	if (lineFeatures.length > 1) {
		const coordinates = lineFeatures.map(f => f.geometry.coordinates);
		const props = lineFeatures[0].properties || {};
		return featureCollection([
			feature(
				{
					type: 'MultiLineString',
					coordinates
				},
				props
			)
		]);
	}

	// If nothing to normalize or only one feature of supported type, return original
	return fc;
}

export const mergeFeatureCollectionToSingleFeature = fc => {
	if (!fc || fc.type !== 'FeatureCollection' || !Array.isArray(fc.features)) {
		throw new Error('Input must be a valid GeoJSON FeatureCollection');
	}

	const features = fc.features;
	if (features.length <= 1) return fc;

	const supported = ['Point', 'LineString', 'Polygon'];
	const typeGroups = features.map(f => f.geometry?.type);

	// Determine base type
	let base = null;
	for (let t of supported) {
		if (typeGroups.some(gt => gt === t || gt === `Multi${t}`)) {
			base = t;
			break;
		}
	}

	if (!base) {
		throw new Error('Unsupported geometry types for merging');
	}

	const mergedCoordinates = features.flatMap(f => {
		const { type, coordinates } = f.geometry;

		if (type === base) {
			return [coordinates]; // wrap
		}
		if (type === `Multi${base}`) {
			return coordinates;
		}
		throw new Error(`Geometry type mismatch: ${type}`);
	});

	return {
		type: 'FeatureCollection',
		features: [
			{
				type: 'Feature',
				geometry: {
					type: `Multi${base}`,
					coordinates: mergedCoordinates
				},
				properties: {}
			}
		]
	};
};

export const convertEsriToGeoJSON = esriFeatures => {
	const fromProjection = 'EPSG:3857'; // Web Mercator
	const toProjection = 'EPSG:4326'; // WGS84 (lon/lat)
	return {
		type: 'FeatureCollection',
		features: esriFeatures.map(f => {
			const geo = arcgisToGeoJSON(f);

			if (geo.geometry && geo.geometry.coordinates) {
				geo.geometry.coordinates = proj4(fromProjection, toProjection, geo.geometry.coordinates);
			}

			return geo;
		})
	};
};

export const normalizeLongitude = long => {
	// Longitude values should be in the range [-180, 180] degrees
	// But sometimes we get values outside this range (e.g., 270, -270, 540, etc.) due to multiple world copies shown on the map
	// This function normalizes any longitude to the standard range

	// Step 1: Shift the coordinate system so we work with positive numbers
	// Convert from [-180, 180] range to [0, 360] range by adding 180
	// Example: -90° becomes 90°, 0° becomes 180°, 90° becomes 270°
	const shifted = long + 180;

	// Step 2: Use modulo to wrap values into [0, 360) range
	// The double modulo trick handles negative numbers correctly:
	// - First modulo gives us a result in range (-360, 360)
	// - Adding 360 ensures we have a positive number
	// - Second modulo brings it back to [0, 360) range
	// Example: -90° + 180° = 90°, 90° % 360 = 90°, (90° + 360°) % 360 = 90°
	const positiveShifted = ((shifted % 360) + 360) % 360;

	// Step 3: Shift back to the standard longitude range [-180, 180)
	// Subtract the 180 we added in step 1
	// Example: 90° - 180° = -90° (which is our normalized result)
	return positiveShifted - 180;
};
